import type { CatalogMasterTable } from '../../db/queries.js';

/**
 * The mould record, declared once.
 *
 * The create form, the edit form and the detail view all render from this array.
 * When the client adds a spec field in week 2 it is one entry here, not three
 * screens to keep in step.
 *
 * `onNameplate` is not decoration: those five values get *engraved into metal*
 * and bolted to a mould for its service life. Flagging them in the UI is the
 * cheapest possible guard against a typo becoming permanent.
 */

export type FieldKind = 'text' | 'textarea' | 'number' | 'date' | 'select' | 'master' | 'photo' | 'yesno';

export interface FieldDef {
  name: string;
  label: string;
  kind: FieldKind;
  /** For `kind: 'master'` - which master table supplies the options. */
  master?: CatalogMasterTable;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  hint?: string;
  required?: boolean;
  /** This value is engraved on the physical nameplate. */
  onNameplate?: boolean;
  /** Full width in the two-column form grid. */
  wide?: boolean;
}

export interface FieldSection {
  id: string;
  title: string;
  description?: string;
  fields: FieldDef[];
}

const ASSET_TYPES = [
  { value: 'MOULD', label: 'Mould' },
  { value: 'DIE', label: 'Die' },
  { value: 'TOOL', label: 'Tool' },
  { value: 'FIXTURE', label: 'Fixture' },
];

export const MOULD_SECTIONS: FieldSection[] = [
  {
    id: 'identity',
    title: 'Identity',
    description: 'The project decides the mould ID, which becomes the QR payload.',
    fields: [
      {
        name: 'catalogNo',
        label: 'S.NO',
        kind: 'number',
        placeholder: 'Next number',
        hint: 'Position in the mould list. Left blank, the next free number is used.',
      },
      {
        name: 'projectId',
        label: 'Project',
        kind: 'master',
        master: 'projects',
        hint: 'Leave the mould ID blank and it is allocated from this project code.',
      },
      {
        name: 'assetCode',
        label: 'Mould ID',
        kind: 'text',
        placeholder: 'Auto-generated from the project',
        hint: 'Cannot be changed once the nameplate is engraved.',
        onNameplate: true,
      },
      { name: 'name', label: 'Mould name', kind: 'text', required: true, onNameplate: true },
      {
        name: 'qrNumber',
        label: 'QR number',
        kind: 'text',
        placeholder: 'e.g. QR-0001',
        hint: "The client's own QR number, if they use one. The QR code itself is made from the mould.",
      },
      { name: 'type', label: 'Asset type', kind: 'select', options: ASSET_TYPES },
      { name: 'photoUrl', label: 'Photo', kind: 'photo', wide: true },
      { name: 'description', label: 'Notes', kind: 'textarea', wide: true },
    ],
  },
  {
    id: 'ownership',
    title: 'Ownership',
    description: 'Who owns the tool and who built it.',
    fields: [
      {
        name: 'ownerCompanyId',
        label: 'Mould owner',
        kind: 'master',
        master: 'companies',
        // "Property Ownership:" on the plate.
        onNameplate: true,
      },
      {
        name: 'customerCompanyId',
        label: 'Customer',
        kind: 'master',
        master: 'companies',
        hint: 'Who the tool makes parts for. Often the owner, not always.',
      },
      {
        name: 'manufactureCompanyId',
        label: 'Manufacturing company',
        kind: 'master',
        master: 'companies',
      },
      { name: 'designerId', label: 'Designer', kind: 'master', master: 'designers' },
    ],
  },
  {
    id: 'construction',
    title: 'Construction',
    fields: [
      { name: 'toolTypeId', label: 'Tool type', kind: 'master', master: 'toolTypes' },
      {
        name: 'cavities',
        label: 'No. of cavities',
        kind: 'text',
        placeholder: '24',
        onNameplate: true,
        hint: 'Engraved as "Cavity:" on the plate.',
      },
      {
        name: 'runnerTypeId',
        label: 'Runner type',
        kind: 'master',
        master: 'runnerTypes',
      },
      { name: 'gateTypeId', label: 'Gate type', kind: 'master', master: 'gateTypes' },
      { name: 'ejectionTypeId', label: 'Ejection type', kind: 'master', master: 'ejectionTypes' },
      {
        name: 'mechanismTypeId',
        label: 'Sliders / lifters',
        kind: 'master',
        master: 'mechanismTypes',
      },
    ],
  },
  {
    id: 'materials',
    title: 'Materials',
    fields: [
      {
        name: 'coreCavityMaterialId',
        label: 'Core / cavity material',
        kind: 'master',
        master: 'materials',
      },
      {
        name: 'moldBaseMaterialId',
        label: 'Mould base material',
        kind: 'master',
        master: 'materials',
      },
      {
        name: 'materialGradeId',
        label: 'Moulding material / shrinkage',
        kind: 'master',
        master: 'materialGrades',
      },
    ],
  },
  {
    id: 'dimensions',
    title: 'Dimensions & manufacture',
    fields: [
      {
        name: 'moldSize',
        label: 'Mould size',
        kind: 'text',
        placeholder: 'W 380 X L 600 X H 260',
        onNameplate: true,
      },
      {
        name: 'manufactureYear',
        label: 'Manufacturing year',
        kind: 'number',
        // Not a real-looking year: a grey "2024" in an empty box reads as a value.
        placeholder: 'YYYY',
        // Off Design B's plate since 11 Oct 2026; Design A still engraves it.
        hint: 'The year only - e.g. 2024. Engraved on Design A plates only.',
      },
      {
        name: 'ratedLife',
        label: 'Rated life (shots)',
        kind: 'number',
        placeholder: '1000000',
        hint: 'What the tool was designed for. Drives the usage bar on the record.',
      },
      {
        name: 'toolTransfer',
        label: 'Tool transfer',
        kind: 'yesno',
        hint: 'TOOL TRANSFER on the mould list: was the tool transferred in.',
      },
    ],
  },
];

/** Flat list, for validation and diffing. */
export const MOULD_FIELDS: FieldDef[] = MOULD_SECTIONS.flatMap((s) => s.fields);

/** The values engraved into metal. */
export const NAMEPLATE_FIELDS = MOULD_FIELDS.filter((f) => f.onNameplate);

/** Detail-view key for a field: the hydrated display name, or the raw value. */
export function displayKey(field: FieldDef): string {
  if (field.kind !== 'master') return field.name;
  // `projectId` → `projectName`, matching the hydration in db/queries.ts
  return `${field.name.replace(/Id$/, '')}Name`;
}
